import { useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import {
    MdEdit,
    MdClose,
    MdUndo,
    MdInfoOutline,
    MdCalculate,
} from 'react-icons/md'
import { mask, unMask } from '@/lib/form-state/util'
import { ExamSelectedItem } from '../types'
import { ProfessionalSearchSelector } from '@/components/ProfessionalSearchSelector'
import { PartnerSelector } from '@/components/partner/PartnerSelector'
import PaymentOptions from '@/components/billing/payment-options'
import { PaymentQuoteOption } from '@/lib/api/financial/financial-billing-api'
import {
    previewAppointmentAdjustment,
    AdjustmentPreviewBilling,
    AdjustmentPreviewSource,
} from '@/lib/billing/appointment-adjustment-preview'
import { isClosedBillingStatus } from '@/lib/billing/appointment-billing-state'
import { profitMargin } from '@/lib/billing/financials'

export type PriceComposition = 'item' | 'partner' | 'professional' | 'sum'

interface PricingSectionProps {
    basePrice: number
    catalogPricingMode: string
    catalogProfessionals: any[]
    catalogPartners: any[]
    professionalId: string
    onProfessionalIdChange: (value: string) => void
    partnerId: number | null
    onPartnerIdChange: (value: number | null) => void
    /** CPF do parceiro escolhido: esconde o profissional que é a mesma pessoa. */
    partnerDocument?: string | null
    discount: string
    onDiscountChange: (value: string) => void
    /**
     * Teto de desconto do atendimento, em porcentagem. **0 = desconto proibido**
     * (nenhum item tem teto configurado no catálogo) e o campo nem aparece.
     * Ver `catalog-max-discount.ts`.
     */
    maxDiscount: number
    /**
     * Libera a edição manual do valor final. Só é `true` em PROCEDIMENTO no modo
     * ORÇAMENTO — exame e consulta valem o preço do catálogo, e o backend recusa
     * `overridePrice` nos dois.
     */
    canEditFinalPrice?: boolean
    isEditing: boolean
    /**
     * Forma de pagamento combinada com o pagador. Fechá-la aqui é o que permite a
     * cobrança nascer pronta — com o método e as parcelas gravados, o link já pode ser
     * enviado sem passar pela tela de cobrança.
     */
    paymentMethod?: string | null
    installmentCount?: number | null
    /**
     * Este atendimento vai somar numa cobrança que já existe (o "Adicionar outro"
     * virou um carrinho).
     *
     * Nesse caso a forma de pagamento **não** é oferecida aqui: quem paga é a cobrança
     * inteira, no fim, com o total de todos os atendimentos. Perguntar por atendimento
     * daria ao operador a impressão de estar fechando o pagamento de um item — e o
     * primeiro método escolhido travaria a soma dos seguintes
     * (`attachAppointmentToBilling` recusa cobrança que já tem método).
     */
    groupingIntoBilling?: boolean
    onPaymentChoice?: (method: string, installments: number) => void
    /** "Decidir na tela de pagamento": limpa a forma e retoma o fluxo antigo. */
    onPaymentCustom?: () => void
    /**
     * "Gerar cobrança depois": agenda sem emitir cobrança nenhuma.
     *
     * Ver `onDefer` no `PaymentOptions`. Ausente, a opção não aparece.
     */
    onPaymentDefer?: () => void
    /** O "cobrar depois" já está escolhido (a tela de agendamento é dona do estado). */
    deferBillingSelected?: boolean
    hidePartnerSelector: boolean
    /**
     * Esconde o seletor de profissional.
     *
     * Usado na INTERMEDIAÇÃO, onde quem executa é o parceiro e escolher também um
     * profissional abriria uma segunda fatia de repasse na mesma cobrança. Só o
     * procedimento cirúrgico continua pedindo profissional — ver
     * `hideProfessionalSelector` em `scheduling-form-page.tsx`.
     */
    hideProfessionalSelector?: boolean
    hasCatalogSelected: boolean
    priceComposition: PriceComposition
    onPriceCompositionChange: (value: PriceComposition) => void
    // Exame: múltiplos itens - para calcular preço somado por parceiro
    examItems?: ExamSelectedItem[]
    /** Itens do procedimento (multi-item, com quantidade própria). */
    orderItems?: Array<{
        catalogId: number
        quantity: number
        unitPrice: number
        catalogVariationId?: number | null
    }>
    /** Consulta com variações escolhidas — elas substituem o preço. */
    hasConsultationVariations?: boolean
    catalogs?: any[]
    serviceType?: string
    // Orçamento: permite editar valor final
    isBudgetMode?: boolean
    overridePrice: number | null
    /**
     * Valor do cupom aplicado (centavos). Entra DEPOIS do desconto manual e do valor
     * digitado: o campo editável continua sendo o preço manual, e a cobrança, a forma de
     * pagamento e o aviso de ajuste olham o valor já com o cupom.
     */
    couponAmount?: number
    onOverridePriceChange: (value: number | null) => void
    // Quando true (dados já carregados), mudar qualquer valor que compõe o preço
    // descarta o valor final editado manualmente e volta a somar. Fica false durante
    // o carregamento (edição) para NÃO apagar um valor customizado salvo.
    pricingReady?: boolean
    /**
     * O valor final pode se ajustar sozinho quando a composição muda.
     *
     * `false` em PAGAMENTO FLEX: ali o paciente já pagou parte do combinado, e o
     * valor é o acordo — trocar o procedimento, a taxa do hospital ou um material
     * não redefine sozinho quanto ele deve. Quem decide o novo valor é o operador,
     * digitando; a tela mostra a composição atualizada ao lado para ele comparar.
     */
    autoAdjustFinalPrice?: boolean
    /**
     * O operador já mexeu em alguma coisa desta edição.
     *
     * `false` significa "a tela ainda está exatamente como foi carregada": nada
     * recalculado pode alterar o Valor Final vindo do banco, e o bloco de custo/markup
     * mostra o que está gravado (`savedCostPrice`/`savedMarkup`). Numa criação chega
     * sempre `true`. Ver a nota em `catalogsHydrating` no formulário.
     */
    pricingTouched?: boolean
    /** Custo gravado no registro em edição, em centavos. */
    savedCostPrice?: number | null
    /** Markup% gravado no registro em edição. */
    savedMarkup?: number | null
    /**
     * Procedimento aprovado em pagamento flex: forma e parcela são escolhidas pelo
     * paciente no portal, a cada pagamento avulso — não há o que decidir aqui.
     */
    hidePaymentOptions?: boolean
    /**
     * Consulta de RETORNO que a casa não cobra (Configurações Financeiras >
     * Cobrança > "Cobrar consulta de retorno", desligada por padrão).
     *
     * Substitui as formas de pagamento por um aviso: nenhuma cobrança nasce, e
     * oferecer Pix e cartão seria pedir uma decisão que o salvamento descarta. O
     * valor continua gravado no atendimento, e quem precisar cobrar um retorno
     * excepcional usa o "Gerar cobrança" da tela do atendimento.
     */
    returnWithoutBilling?: boolean
    /**
     * Ligar o pagamento FLEX a partir daqui (só procedimento; ausente, a opção não
     * aparece). Ver `onFlex` no `PaymentOptions`.
     */
    onEnableFlex?: () => void
    flexSelected?: boolean
    /**
     * O valor no campo do profissional veio do CATÁLOGO (materializado ao abrir), não
     * do operador. Nesse caso ele é só o repasse — o preço continua saindo do
     * honorário de venda.
     */
    professionalAmountFromCatalog?: boolean
    // PROCEDIMENTO: o valor calculado é o CUSTO; o Valor Final é a VENDA cobrada.
    // Quando o procedimento NÃO tem preço próprio (sellingPrice em CatalogCompanyPrice),
    // exige que o operador informe a venda (obrigatório).
    requireSellingPrice?: boolean
    // Reporta custo e markup% ao parent para gravar em Order.costPrice/markupPercentage.
    onCostMarkupChange?: (cost: number, markupPercentage: number | null) => void
    // Order: total dos exames atrelados (somado quando partner/professional escolhido)
    extraTotal?: number
    // Order: valor cobrado pelo hospital (diárias, taxas) — sempre aditivo
    hospitalFee?: number
    // Order: honorário do anestesista — aditivo (cirurgia: preços separados)
    anesthetistFee?: number
    // PROCEDIMENTO: override editável do valor de repasse ao profissional (string de moeda
    // formatada, ''=sem override → usa o valor do catálogo). Médicos nem sempre cobram o
    // mesmo por procedimento. Entra na SOMA do total e é gravado no Order.
    professionalAmountOverride?: string
    /**
     * `fromCatalog` diz que o valor foi MATERIALIZADO do catálogo (custo do vínculo),
     * não digitado pelo operador — a tela guarda isso para não deixar esse número
     * mexer no preço ao paciente.
     */
    onProfessionalAmountOverrideChange?: (
        value: string,
        fromCatalog?: boolean
    ) => void
    // Notifica o parent quando a barra flutuante esta visivel, para aplicar padding
    onFloatingBarChange?: (visible: boolean) => void
    // Reporta o valor que sera cobrado (ja com override e desconto) para o resumo
    // fixo do topo da pagina — sem isso o operador precisa rolar ate aqui so para
    // conferir quanto vai sair.
    onSubmittedPriceChange?: (cents: number) => void
    /**
     * Quanto o pagador realmente paga na forma escolhida (taxa embutida), ou `null`
     * quando não há forma definida. Vai para o resumo fixo do topo — que antes anunciava
     * o valor do serviço mesmo depois de o operador fechar "3x no cartão", e os dois
     * números não batiam.
     */
    onPaymentQuoteChange?: (option: PaymentQuoteOption | null) => void
    // Edição: cobrança já existente do atendimento. Com dinheiro pago, mexer no valor não
    // "corrige o preço" — gera cobrança complementar ou estorno. O operador precisa saber
    // disso ANTES de salvar (ver `previewAppointmentAdjustment`).
    existingBilling?: AdjustmentPreviewBilling | null
    // Qual atendimento está sendo editado. Indispensável quando a cobrança cobre
    // vários: o aviso compara contra a FATIA deste, não contra o total do grupo.
    adjustmentSource?: AdjustmentPreviewSource | null
}

const formatCentsLabel = (cents: number): string =>
    `R$ ${(cents / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

/** Como a forma de pagamento é falada ao pagador. */
const PAYMENT_METHOD_LABEL: Record<string, string> = {
    PIX: 'Pix',
    CREDIT_CARD: 'cartão de crédito',
    DEBIT_CARD: 'cartão de débito',
    BOLETO: 'boleto',
}

/**
 * "Total no Pix", "Total em 3x no cartão de crédito" — o que o operador fala.
 */
const payerTotalLabel = (option: PaymentQuoteOption): string => {
    const method = PAYMENT_METHOD_LABEL[option.method] ?? option.method
    return option.installments > 1
        ? `Total em ${option.installments}x no ${method}`
        : option.method === 'PIX'
          ? 'Total no Pix'
          : `Total no ${method}`
}

/**
 * Aviso ao lado do Valor Final: o que a mudança de valor vai provocar na cobrança já paga.
 *
 * `compact` é a versão da barra flutuante, onde não cabe o texto inteiro.
 */
function BillingAdjustmentNotice({
    action,
    amount,
    currentTotal,
    newTotal,
    shared = false,
    groupCurrentTotal,
    groupNewTotal,
    groupCount,
    compact = false,
    coveredByHealthPlan = false,
}: {
    action: 'EXTRA_CHARGE' | 'REFUND' | 'UPDATED'
    amount: number
    currentTotal: number
    newTotal: number
    /** Cobrança compartilhada: os valores acima são os DESTE atendimento. */
    shared?: boolean
    groupCurrentTotal?: number
    groupNewTotal?: number
    groupCount?: number
    compact?: boolean
    /** Coberto por convênio: o valor não é cobrado do paciente, é faturado ao convênio. */
    coveredByHealthPlan?: boolean
}) {
    const tone =
        action === 'EXTRA_CHARGE'
            ? 'border-warning/40 bg-warning/10 text-warning-content'
            : action === 'REFUND'
              ? 'border-info/40 bg-info/10 text-info-content'
              : 'border-base-300 bg-base-200 text-base-content'

    const shortLabel =
        action === 'EXTRA_CHARGE'
            ? `Nova cobrança de ${formatCentsLabel(amount)}`
            : action === 'REFUND'
              ? `Estorno de ${formatCentsLabel(amount)}`
              : shared
                ? // Compartilhada: dizer "cobrança atualizada para X" seria falso — X é
                  // o valor deste atendimento, não o da cobrança.
                  `Este atendimento: ${formatCentsLabel(newTotal)} (cobrança: ${formatCentsLabel(groupNewTotal ?? 0)})`
                : coveredByHealthPlan
                  ? `Faturado ao convênio: ${formatCentsLabel(newTotal)}`
                  : `Cobrança atualizada para ${formatCentsLabel(newTotal)}`

    if (compact) {
        return (
            <span
                className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-medium ${tone}`}
            >
                <MdInfoOutline className="text-sm shrink-0" />
                {shortLabel}
            </span>
        )
    }

    return (
        <div
            className={`mt-2 flex items-start gap-2 rounded-lg border px-3 py-2 text-sm ${tone}`}
        >
            <MdInfoOutline className="mt-0.5 text-base shrink-0" />
            <div>
                {action === 'EXTRA_CHARGE' && (
                    <>
                        <span className="font-semibold">
                            Será criada uma nova cobrança de{' '}
                            {formatCentsLabel(amount)}
                        </span>{' '}
                        ao salvar — o atendimento já tem cobrança paga de{' '}
                        {formatCentsLabel(currentTotal)}, e a diferença é
                        cobrada à parte.
                    </>
                )}
                {action === 'REFUND' && (
                    <>
                        <span className="font-semibold">
                            Será solicitado um estorno de{' '}
                            {formatCentsLabel(amount)}
                        </span>{' '}
                        ao salvar — o atendimento já tem cobrança paga de{' '}
                        {formatCentsLabel(currentTotal)}, e a diferença volta ao
                        paciente após aprovação.
                    </>
                )}
                {action === 'UPDATED' && !shared && coveredByHealthPlan && (
                    <>
                        <span className="font-semibold">
                            O valor faturado ao convênio passa de{' '}
                            {formatCentsLabel(currentTotal)} para{' '}
                            {formatCentsLabel(newTotal)}
                        </span>{' '}
                        ao salvar — nada é cobrado do paciente, e o novo valor
                        entra na fatura do período.
                    </>
                )}
                {action === 'UPDATED' && !shared && !coveredByHealthPlan && (
                    <>
                        <span className="font-semibold">
                            A cobrança será atualizada de{' '}
                            {formatCentsLabel(currentTotal)} para{' '}
                            {formatCentsLabel(newTotal)}
                        </span>{' '}
                        ao salvar — ela ainda não foi paga, então o valor é
                        apenas corrigido (sem cobrança nova nem estorno).
                    </>
                )}
                {action === 'UPDATED' && shared && (
                    <>
                        <span className="font-semibold">
                            Este atendimento passa de{' '}
                            {formatCentsLabel(currentTotal)} para{' '}
                            {formatCentsLabel(newTotal)}
                        </span>{' '}
                        ao salvar. A cobrança é compartilhada com outros{' '}
                        {(groupCount ?? 2) - 1} atendimento
                        {(groupCount ?? 2) - 1 > 1 ? 's' : ''} e o total dela
                        vai de {formatCentsLabel(groupCurrentTotal ?? 0)} para{' '}
                        {formatCentsLabel(groupNewTotal ?? 0)} — ela ainda não
                        foi paga, então o valor é apenas corrigido.
                    </>
                )}
            </div>
        </div>
    )
}

/**
 * Qual lado do vínculo ler: o que se COBRA (venda) ou o que se PAGA (custo).
 *
 * `CatalogPartner`/`CatalogProfessional` guardam os dois — `price` é o custo,
 * `sellingPrice` é a venda (regra de ouro em `docs/catalog-system.md`). Os dois lados
 * têm consumidor no backend, e confundi-los é caro:
 *
 * - **venda** (`sellingPrice ?? price`) é o que entra no preço cobrado do paciente
 *   (`partnerFee`/`professionalFee` em `order.service`);
 * - **custo** (`price`) é o que vira REPASSE ao destinatário
 *   (`PayoutSnapshotService.resolvePartnerAmountForItems`, que lê só `price`).
 *
 * O procedimento precisa dos dois na mesma tela: o custo para medir a margem, a venda
 * para cobrar. Antes disto a tela somava a VENDA e a exibia rotulada como "Custo" — o
 * markup dava sempre ~0% e o custo gravado em `Order.costPrice` não tinha relação
 * com o dinheiro que sai.
 */
type PriceBasis = 'selling' | 'cost'

function linkPrice(link: any, basis: PriceBasis): number {
    if (!link) return 0
    return basis === 'cost'
        ? link.price || 0
        : link.sellingPrice || link.price || 0
}

/**
 * Total do parceiro somando ITEM A ITEM, multiplicado pela quantidade.
 *
 * ⚠️ Cada unidade é um atendimento a mais: o parceiro executa duas vezes e
 * recebe duas vezes. O backend faz exatamente isto (`partnerFee += preço *
 * item.quantity` em exam/order.service) — se a tela não multiplicar, o
 * operador fecha o agendamento por um valor e o sistema grava outro.
 */
export function getPartnerTotalForItems(
    partnerId: number,
    examItems: Array<{
        catalogId: number
        quantity: number
        unitPrice: number
        catalogVariationId?: number | null
    }>,
    catalogs: any[],
    basis: PriceBasis = 'selling'
): number {
    return examItems.reduce((sum, item) => {
        // Variação tem preço próprio flat por filial: substitui o base e NÃO
        // varia por parceiro. Usa o unitPrice da linha (preço da variação).
        if (item.catalogVariationId) {
            return sum + (item.unitPrice || 0) * item.quantity
        }
        const cat = catalogs.find((c: any) => c.id === item.catalogId)
        const cp = (cat?.partners || []).find(
            (p: any) => p.partnerId === partnerId && p.active !== false
        )
        // Sem vínculo com este parceiro não há custo dele a somar — no cálculo de
        // venda, a linha cai no preço do próprio item.
        const price = cp
            ? linkPrice(cp, basis)
            : basis === 'cost'
              ? 0
              : item.unitPrice || 0
        return sum + price * item.quantity
    }, 0)
}

/** Total do profissional, item a item e vezes a quantidade. Ver o gêmeo acima. */
function getProfessionalTotalForItems(
    professionalId: number,
    examItems: Array<{
        catalogId: number
        quantity: number
        unitPrice: number
        catalogVariationId?: number | null
    }>,
    catalogs: any[],
    basis: PriceBasis = 'selling'
): number {
    return examItems.reduce((sum, item) => {
        // Variação tem preço próprio flat: não varia por profissional.
        if (item.catalogVariationId) {
            return sum + (item.unitPrice || 0) * item.quantity
        }
        const cat = catalogs.find((c: any) => c.id === item.catalogId)
        const cp = (cat?.professionals || []).find(
            (p: any) =>
                p.professionalId === professionalId && p.active !== false
        )
        const price = cp
            ? linkPrice(cp, basis)
            : basis === 'cost'
              ? 0
              : item.unitPrice || 0
        return sum + price * item.quantity
    }, 0)
}

export function getEffectivePrice(
    basePrice: number,
    catalogPricingMode: string,
    partnerId: number | null,
    professionalId: string,
    catalogPartners: any[],
    catalogProfessionals: any[],
    priceComposition: PriceComposition,
    examItems?: ExamSelectedItem[],
    catalogs?: any[],
    serviceType?: string,
    extraTotal: number = 0,
    hospitalFee: number = 0,
    anesthetistFee: number = 0,
    professionalAmountOverride: number | null = null,
    /** Itens do procedimento — o parceiro/profissional soma por item e quantidade. */
    orderItems?: Array<{
        catalogId: number
        quantity: number
        unitPrice: number
        catalogVariationId?: number | null
    }>,
    /** Consulta com variações escolhidas: elas SUBSTITUEM o preço (regra do backend). */
    hasConsultationVariations: boolean = false,
    /**
     * `cost` lê o CUSTO do vínculo (`price`) em vez da venda — é o que o repasse paga
     * ao parceiro/profissional. Só o procedimento usa, para medir a margem.
     */
    basis: PriceBasis = 'selling'
): number {
    if (catalogPricingMode === 'item_only') return basePrice + hospitalFee

    // Itens com quantidade própria. Exame e procedimento são multi-item; a
    // consulta não tem itens (usa variações, que já entram no `basePrice`).
    const itensComQuantidade =
        serviceType === 'EXAM'
            ? examItems
            : serviceType === 'PROCEDURE'
              ? orderItems
              : undefined
    const porItem = !!itensComQuantidade?.length && !!catalogs?.length

    const partnerPrice = partnerId
        ? porItem
            ? getPartnerTotalForItems(
                  partnerId,
                  itensComQuantidade!,
                  catalogs!,
                  basis
              )
            : (() => {
                  const p = catalogPartners.find(
                      (cp: any) => cp.partnerId === partnerId
                  )
                  return linkPrice(p, basis)
              })()
        : 0

    const profId = professionalId ? parseInt(professionalId) : null
    const profPrice = profId
        ? porItem
            ? getProfessionalTotalForItems(
                  profId,
                  itensComQuantidade!,
                  catalogs!,
                  basis
              )
            : (() => {
                  const p = catalogProfessionals.find(
                      (cp: any) => cp.professionalId === profId
                  )
                  return linkPrice(p, basis)
              })()
        : 0

    // CONSULTA com variações: elas substituem o preço, inclusive o do parceiro —
    // é o que `consultation.service` faz ao gravar. Sem isto, escolher variação
    // com quantidade 2 não mexia no total, porque o preço do parceiro vencia.
    if (serviceType === 'CONSULTATION' && hasConsultationVariations) {
        return basePrice + hospitalFee
    }

    // Procedimento (cirurgia): preços SEPARADOS — tudo soma. basePrice já inclui a
    // base/variação + exames/consultas atrelados + materiais. Profissional, parceiro,
    // anestesista e hospital somam por cima (não substituem a base como em exame).
    if (serviceType === 'PROCEDURE') {
        // Override do valor do profissional (editado no agendamento) tem prioridade.
        const effProf =
            professionalAmountOverride != null
                ? professionalAmountOverride
                : profPrice
        return basePrice + partnerPrice + effProf + anesthetistFee + hospitalFee
    }

    if (partnerId && professionalId && partnerPrice > 0 && profPrice > 0) {
        switch (priceComposition) {
            case 'sum':
                return partnerPrice + profPrice + extraTotal + hospitalFee
            case 'partner':
                return partnerPrice + extraTotal + hospitalFee
            case 'professional':
                return profPrice + extraTotal + hospitalFee
            case 'item':
                return basePrice + hospitalFee
            default:
                return basePrice + hospitalFee
        }
    }

    if (partnerId && partnerPrice > 0)
        return partnerPrice + extraTotal + hospitalFee
    if (professionalId && profPrice > 0)
        return profPrice + extraTotal + hospitalFee
    return basePrice + hospitalFee
}

export default function PricingSection({
    basePrice,
    catalogPricingMode,
    catalogProfessionals,
    catalogPartners,
    professionalId,
    onProfessionalIdChange,
    partnerId,
    onPartnerIdChange,
    partnerDocument,
    discount,
    onDiscountChange,
    maxDiscount,
    canEditFinalPrice = false,
    isEditing,
    paymentMethod = null,
    installmentCount = null,
    groupingIntoBilling = false,
    isBudgetMode = false,
    onPaymentChoice,
    onPaymentCustom,
    onPaymentDefer,
    deferBillingSelected = false,
    hidePartnerSelector,
    hideProfessionalSelector,
    hasCatalogSelected,
    priceComposition,
    onPriceCompositionChange,
    examItems,
    orderItems,
    hasConsultationVariations = false,
    catalogs,
    serviceType,
    overridePrice,
    couponAmount = 0,
    onOverridePriceChange,
    pricingReady = true,
    pricingTouched = true,
    autoAdjustFinalPrice = true,
    savedCostPrice = null,
    savedMarkup = null,
    hidePaymentOptions = false,
    returnWithoutBilling = false,
    onEnableFlex,
    flexSelected = false,
    professionalAmountFromCatalog = false,
    requireSellingPrice = false,
    onCostMarkupChange,
    extraTotal = 0,
    hospitalFee = 0,
    anesthetistFee = 0,
    professionalAmountOverride = '',
    onProfessionalAmountOverrideChange,
    onFloatingBarChange,
    onSubmittedPriceChange,
    onPaymentQuoteChange,
    existingBilling,
    adjustmentSource,
}: PricingSectionProps) {
    const [editingPrice, setEditingPrice] = useState(false)
    const [editPriceValue, setEditPriceValue] = useState('')
    const [finalRowEl, setFinalRowEl] = useState<HTMLDivElement | null>(null)
    const [finalRowVisible, setFinalRowVisible] = useState(true)

    useEffect(() => {
        if (!finalRowEl || typeof IntersectionObserver === 'undefined') return
        const observer = new IntersectionObserver(
            entries => {
                const entry = entries[0]
                if (entry) setFinalRowVisible(entry.isIntersecting)
            },
            { threshold: 0, rootMargin: '0px 0px -8px 0px' }
        )
        observer.observe(finalRowEl)
        return () => observer.disconnect()
    }, [finalRowEl])

    const currencyFormat = (
        value: string | number | null | undefined,
        allowEmpty = false
    ) => {
        if (value === null || value === undefined) value = '0'
        const digits = `${value}`.replace(/[^0-9]+/g, '')
        if (
            allowEmpty &&
            (!digits || digits === '0' || digits === '00' || digits === '000')
        )
            return ''
        return mask(digits, '', 'currency', {
            prefix: 'R$ ',
            decimalSeparator: ',',
            groupSeparator: '.',
            precision: 2,
        })
    }
    const parseCurrencyToCents = (value: string): number => {
        if (!value.trim()) return 0
        return parseInt(unMask(value, 'currency'))
    }
    const isItemOnly = catalogPricingMode === 'item_only'
    const isExam = serviceType === 'EXAM' && examItems && catalogs
    /**
     * Itens com quantidade própria (exame e procedimento são multi-item). É o que
     * faz o preço de parceiro/profissional somar por item e vezes a quantidade,
     * como o backend grava. Consulta não tem itens — usa variações no basePrice.
     */
    const itensComQuantidade =
        catalogs?.length && serviceType === 'EXAM'
            ? examItems
            : catalogs?.length && serviceType === 'PROCEDURE'
              ? orderItems
              : undefined

    // Parceiro/profissional NÃO atrelado ao item vale 0 aqui — é seleção livre
    // (ver `showProfessionalSelector`), e sem vínculo não há preço de catálogo a
    // usar. O total cai no preço base do item; em procedimento, o operador digita
    // o valor no campo "Valor do profissional".
    const partnerPrice = useMemo(() => {
        if (!partnerId) return 0
        if (itensComQuantidade)
            return getPartnerTotalForItems(
                partnerId,
                itensComQuantidade,
                catalogs!
            )
        const p = catalogPartners.find((cp: any) => cp.partnerId === partnerId)
        return p?.sellingPrice || p?.price || 0
    }, [partnerId, catalogPartners, itensComQuantidade, catalogs])

    const profPrice = useMemo(() => {
        if (!professionalId) return 0
        const profId = parseInt(professionalId)
        if (itensComQuantidade)
            return getProfessionalTotalForItems(
                profId,
                itensComQuantidade,
                catalogs!
            )
        const p = catalogProfessionals.find(
            (cp: any) => cp.professionalId === profId
        )
        return p?.sellingPrice || p?.price || 0
    }, [professionalId, catalogProfessionals, itensComQuantidade, catalogs])

    /**
     * O que o profissional RECEBE pelo vínculo — o custo, não a venda.
     *
     * `profPrice` acima é o preço de VENDA do honorário: é ele que entra na composição
     * do preço ao paciente. O campo "Valor do profissional", porém, é gravado em
     * `Order.professionalAmount`, que é o REPASSE — e o backend o resolve pelo
     * `CatalogProfessional.price` (`resolveProfessionalAmountForItems`).
     *
     * ⚠️ Os dois divergem em 56 dos ~5.000 vínculos de produção, em até R$ 105 por
     * item. Materializar a VENDA no campo faria o profissional receber o preço de
     * venda: R$ 350 onde o combinado é R$ 245, só por abrir a edição e salvar.
     */
    const profCostPrice = useMemo(() => {
        if (!professionalId) return 0
        const profId = parseInt(professionalId)
        if (itensComQuantidade)
            return getProfessionalTotalForItems(
                profId,
                itensComQuantidade,
                catalogs!,
                'cost'
            )
        const p = catalogProfessionals.find(
            (cp: any) => cp.professionalId === profId
        )
        return p?.price || 0
    }, [professionalId, catalogProfessionals, itensComQuantidade, catalogs])

    /**
     * ABRIR A EDIÇÃO MATERIALIZA o valor do profissional no campo.
     *
     * O campo vazio significa "usa o valor do catálogo", e o número aparece só como
     * placeholder. Isso funciona enquanto o item não muda — mas quem troca o item do
     * catálogo troca também o vínculo de onde esse número sai: o profissional que
     * recebia R$ 5.000 pela histeroscopia diagnóstica não tem vínculo com a
     * cirúrgica, e o valor **cai para zero** ao salvar, sem ninguém ter pedido e sem
     * nada em tela dizendo.
     *
     * Escrevendo o número no campo ao abrir, ele passa a ser do ATENDIMENTO e não do
     * catálogo: a troca de item não o leva junto, e quem quiser o valor do item novo
     * apaga o campo (o "Restaurar valor do catálogo" continua ali para isso).
     *
     * ⚠️ Uma vez só, e só na edição. Refazer a cada render desfaria o "restaurar" no
     * instante seguinte ao clique; e na CRIAÇÃO o campo vazio é o certo — ali o
     * catálogo é a fonte, e ainda não há atendimento cujo acerto preservar.
     */
    const professionalAmountMaterialized = useRef(false)
    useEffect(() => {
        if (
            !isEditing ||
            serviceType !== 'PROCEDURE' ||
            !pricingReady ||
            professionalAmountMaterialized.current
        ) {
            return
        }
        // Sem profissional (ou sem preço no vínculo) não há o que congelar; e com o
        // campo já preenchido o valor é do operador, que manda de qualquer forma.
        if (
            !professionalId ||
            profCostPrice <= 0 ||
            professionalAmountOverride
        ) {
            // Só marca como resolvido quando havia dado suficiente para decidir —
            // senão a hidratação tardia do catálogo perderia a janela.
            if (professionalId && professionalAmountOverride)
                professionalAmountMaterialized.current = true
            return
        }
        professionalAmountMaterialized.current = true
        onProfessionalAmountOverrideChange?.(
            currencyFormat(profCostPrice.toString(), true),
            // Veio do catálogo, não do operador: o preço ao paciente continua saindo
            // do honorário de VENDA, e só o repasse usa este número.
            true
        )
    }, [
        isEditing,
        serviceType,
        pricingReady,
        professionalId,
        profCostPrice,
        professionalAmountOverride,
        onProfessionalAmountOverrideChange,
    ])

    /**
     * Override do valor do profissional (string de moeda) convertido para centavos.
     *
     * ⚠️ O valor MATERIALIZADO do catálogo fica de fora: ele é o custo (o repasse), e
     * a composição do preço ao paciente usa o honorário de VENDA (`profPrice`). Deixá-lo
     * entrar aqui derrubaria o preço do atendimento ao simplesmente abrir a edição,
     * exatamente nos vínculos em que venda e custo divergem.
     *
     * O que o OPERADOR digita continua valendo para os dois: ali ele está dizendo
     * quanto o médico cobra nesta cirurgia — número que entra no preço e é o que o
     * médico recebe.
     */
    const professionalOverrideCents =
        serviceType === 'PROCEDURE' &&
        professionalAmountOverride &&
        !professionalAmountFromCatalog
            ? parseCurrencyToCents(professionalAmountOverride)
            : null
    // Valor efetivo do profissional: em PROCEDIMENTO, o override editado tem prioridade
    // sobre o valor do catálogo. Entra na soma do total e vira o repasse na cobrança.
    const effProfPrice =
        professionalOverrideCents != null
            ? professionalOverrideCents
            : profPrice

    const hasBothSelected =
        !!partnerId && !!professionalId && partnerPrice > 0 && profPrice > 0

    const effectivePrice = useMemo(() => {
        if (isItemOnly) return basePrice + hospitalFee
        return getEffectivePrice(
            basePrice,
            catalogPricingMode,
            partnerId,
            professionalId,
            catalogPartners,
            catalogProfessionals,
            priceComposition,
            examItems,
            catalogs,
            serviceType,
            extraTotal,
            hospitalFee,
            anesthetistFee,
            professionalOverrideCents,
            orderItems,
            hasConsultationVariations
        )
    }, [
        basePrice,
        catalogPricingMode,
        partnerId,
        professionalId,
        catalogPartners,
        catalogProfessionals,
        priceComposition,
        examItems,
        orderItems,
        hasConsultationVariations,
        catalogs,
        serviceType,
        extraTotal,
        hospitalFee,
        anesthetistFee,
        partnerPrice,
        profPrice,
        effProfPrice,
        professionalOverrideCents,
    ])

    /**
     * CUSTO do procedimento: o mesmo cálculo do valor composto, mas lendo o `price`
     * dos vínculos — o campo que vira repasse ao parceiro e ao profissional
     * (`resolvePartnerAmountForItems` lê só ele).
     *
     * Só existe para PROCEDURE: em exame e consulta o valor do vínculo É o preço
     * cobrado, e não há custo separado a exibir.
     *
     * Materiais, hospital, anestesista e atrelados entram nos dois lados porque são
     * repassados como estão (o `basePrice` os inclui) — a diferença entre venda e
     * custo mora em parceiro e profissional, que é onde a margem é feita.
     */
    const orderCostPrice = useMemo(() => {
        if (serviceType !== 'PROCEDURE') return 0
        return getEffectivePrice(
            basePrice,
            catalogPricingMode,
            partnerId,
            professionalId,
            catalogPartners,
            catalogProfessionals,
            priceComposition,
            examItems,
            catalogs,
            serviceType,
            extraTotal,
            hospitalFee,
            anesthetistFee,
            professionalOverrideCents,
            orderItems,
            hasConsultationVariations,
            'cost'
        )
        // Mesmas dependências do `effectivePrice` — os dois leem as mesmas entradas.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        basePrice,
        catalogPricingMode,
        partnerId,
        professionalId,
        catalogPartners,
        catalogProfessionals,
        priceComposition,
        examItems,
        orderItems,
        hasConsultationVariations,
        catalogs,
        serviceType,
        extraTotal,
        hospitalFee,
        anesthetistFee,
        professionalOverrideCents,
    ])

    const finalPrice = useMemo(() => {
        if (effectivePrice <= 0) return 0
        const d = parseFloat(discount)
        if (isNaN(d) || d <= 0) return effectivePrice
        // Arredonda o DESCONTO, não o resultado — idêntico a `resolvePricing` no
        // backend (`base - Math.round((base * d) / 100)`), que é quem grava o valor.
        //
        // Arredondando o resultado, meio centavo caía para o outro lado: 3% de
        // R$ 39,50 dá R$ 38,32 aqui e R$ 38,31 no backend. A tela prometia um
        // valor e a cobrança saía com outro — e, na edição, o aviso de ajuste
        // acusava mudança de R$ 0,01 sem ninguém ter tocado no preço.
        return effectivePrice - Math.round((effectivePrice * d) / 100)
    }, [effectivePrice, discount])

    // O usuário pode editar o Valor Final (overridePrice) e ele funciona como BASE:
    // ao adicionar/remover qualquer coisa que compõe o preço (anestesista, itens,
    // quantidades, profissional, parceiro...), a diferença (delta) do valor calculado
    // é somada/subtraída por cima do valor editado — preservando o ajuste manual.
    // Ex: final editado 10.000, base era 8.000; ao somar anestesista 3.450, vira 13.450.
    // Só age após `pricingReady` para não mexer no valor carregado na edição.
    //
    // ⚠️ Só age depois que o operador mexe em algo (`pricingTouched`). Sem essa trava,
    // qualquer recomputação disparada por dado que chegou tarde era lida como
    // "acrescentaram ao pedido" e engordava o valor final GRAVADO — um procedimento de
    // R$ 19.500 abria valendo R$ 27.700 só de ser reaberto.
    const lastFinalPriceRef = useRef<number | null>(null)
    useEffect(() => {
        if (!pricingReady || !pricingTouched || !autoAdjustFinalPrice) {
            lastFinalPriceRef.current = null
            return
        }
        if (lastFinalPriceRef.current === null) {
            lastFinalPriceRef.current = finalPrice
            return
        }
        if (lastFinalPriceRef.current !== finalPrice) {
            const delta = finalPrice - lastFinalPriceRef.current
            lastFinalPriceRef.current = finalPrice
            if (overridePrice != null) {
                const next = overridePrice + delta
                onOverridePriceChange(next > 0 ? next : null)
            }
        }
    }, [
        finalPrice,
        pricingReady,
        pricingTouched,
        autoAdjustFinalPrice,
        overridePrice,
        onOverridePriceChange,
    ])

    // O valor que será enviado ao backend — é ele que a cobrança vai perseguir.
    const submittedPrice = overridePrice != null ? overridePrice : finalPrice
    // O que a cobrança vai cobrar: o preço manual menos o cupom, nunca negativo.
    const chargedPrice = Math.max(0, submittedPrice - Math.min(couponAmount, submittedPrice))

    useEffect(() => {
        onSubmittedPriceChange?.(submittedPrice)
    }, [submittedPrice, onSubmittedPriceChange])

    /**
     * O total da forma escolhida, com a taxa embutida — vem do backend, via
     * `PaymentOptions`. O valor acima (`submittedPrice`) é o do SERVIÇO: é ele que a
     * cobrança grava e o que o operador edita. O que o pagador desembolsa depende da
     * forma, e é este número.
     */
    const [paymentQuote, setPaymentQuote] = useState<PaymentQuoteOption | null>(
        null
    )
    // Orçamento e "somar na cobrança aberta" não oferecem forma de pagamento: sem isto
    // a última cotação ficaria na tela depois de o componente sair de cena.
    // Flex: quem escolhe forma e parcela é o paciente, pagamento a pagamento, no
    // portal — a escolha feita aqui não teria onde valer.
    // Retorno sem cobrança: o bloco de valores inteiro sai de cena junto com o
    // `PaymentOptions`, e sem limpar a cotação o resumo do formulário continuaria
    // anunciando um "total no Pix" de uma cobrança que não vai nascer.
    const showPaymentOptions =
        !isBudgetMode &&
        !groupingIntoBilling &&
        !hidePaymentOptions &&
        !returnWithoutBilling
    useEffect(() => {
        if (!showPaymentOptions) setPaymentQuote(null)
    }, [showPaymentOptions])
    useEffect(() => {
        onPaymentQuoteChange?.(paymentQuote)
    }, [paymentQuote, onPaymentQuoteChange])
    // Desmontou (trocou o tipo de serviço, por exemplo): quem escuta não pode ficar
    // com a última cotação de um formulário que não existe mais.
    const quoteListenerRef = useRef(onPaymentQuoteChange)
    quoteListenerRef.current = onPaymentQuoteChange
    useEffect(() => () => quoteListenerRef.current?.(null), [])
    /**
     * Só vale mostrar quando o desembolso difere do valor do serviço — sem taxa na
     * forma escolhida, repetir o mesmo número duas vezes só polui.
     *
     * A cotação é assíncrona e o valor muda a cada tecla: enquanto a resposta não chega,
     * `baseAmount` ainda é o do preço anterior, e usar esse total anunciaria um valor
     * que não corresponde ao serviço em tela.
     */
    const payerQuote =
        paymentQuote && paymentQuote.baseAmount === chargedPrice
            ? paymentQuote
            : null

    // PROCEDIMENTO: o valor calculado (effectivePrice) é o CUSTO; o Valor Final (submittedPrice)
    // é a VENDA cobrada. markup% = (venda - custo)/custo. Reportado ao parent (Order.costPrice/
    // markupPercentage). Digitar o markup ajusta a venda: venda = custo × (1 + markup/100).
    const isOrder = serviceType === 'PROCEDURE'
    /**
     * O custo que a tela mostra e reporta ao parent.
     *
     * Vem do `price` dos vínculos (o que vira repasse). O fallback para o valor
     * composto cobre o caso em que nenhum vínculo tem custo cadastrado: ali não há
     * margem mensurável, e é o comportamento anterior — custo igual à venda, markup
     * zero, tudo editável pelo operador.
     */
    const orderCostCalculated =
        isOrder && orderCostPrice > 0 ? orderCostPrice : effectivePrice
    /**
     * Edição ainda intocada: vale o custo GRAVADO, não o recalculado.
     *
     * Um preço de vínculo alterado no cadastro depois da criação faria a tela abrir
     * com um custo — e um markup — diferentes do que foi acertado com o paciente, sem
     * ninguém ter pedido recálculo. O cálculo assume no instante em que o operador
     * toca em qualquer campo.
     */
    const orderCost =
        isOrder &&
        !pricingTouched &&
        savedCostPrice != null &&
        savedCostPrice > 0
            ? savedCostPrice
            : orderCostCalculated
    /**
     * Quando o bloco Custo / Markup / Valor de venda aparece.
     *
     * Além do procedimento sem preço próprio de filial (`requireSellingPrice`), agora
     * também quando o vínculo escolhido **tem custo cadastrado**: é o caso do parceiro
     * com custo e venda salvos, em que o operador precisa ver a margem que já existe em
     * vez de descobrir que o "Custo" na tela era a venda.
     */
    const showOrderMarkup =
        isOrder &&
        (requireSellingPrice ||
            orderCostPrice > 0 ||
            // Edição intocada de um procedimento que já tem custo gravado: o bloco
            // precisa aparecer mesmo antes de os vínculos serem recalculados.
            (!pricingTouched && (savedCostPrice ?? 0) > 0))
    const orderMarkup =
        isOrder && !pricingTouched && savedMarkup != null
            ? savedMarkup
            : isOrder && orderCost > 0
              ? Math.round(((submittedPrice - orderCost) / orderCost) * 10000) /
                100
              : null

    useEffect(() => {
        if (!isOrder) return
        onCostMarkupChange?.(
            showOrderMarkup ? orderCost : 0,
            showOrderMarkup ? orderMarkup : null
        )
    }, [isOrder, showOrderMarkup, orderCost, orderMarkup, onCostMarkupChange])

    /**
     * Input de markup com estado local (permite digitar/limpar livremente). Só
     * sincroniza a partir do valor derivado quando NÃO está em foco — assim apagar não
     * "volta" o número que estava.
     *
     * ⚠️ **Mostra a margem que já existe, não só a digitada.** O campo era preenchido
     * apenas quando havia `overridePrice`: escolher um parceiro com custo e venda
     * salvos deixava o markup em branco, como se não houvesse margem cadastrada — e o
     * operador redigitava um número que o cadastro já tinha.
     */
    const [markupInput, setMarkupInput] = useState('')
    const [markupFocused, setMarkupFocused] = useState(false)
    useEffect(() => {
        if (markupFocused) return
        setMarkupInput(
            orderMarkup != null && orderMarkup !== 0 ? String(orderMarkup) : ''
        )
    }, [orderMarkup, markupFocused])

    const handleMarkupInput = (raw: string) => {
        setMarkupInput(raw)
        if (raw.trim() === '') {
            // Limpou o markup: solta o valor digitado e volta ao calculado (a venda do
            // vínculo/catálogo), de onde o markup exibido passa a ser derivado de novo.
            onOverridePriceChange(null)
            return
        }
        const pct = parseFloat(raw)
        if (orderCost > 0 && !isNaN(pct)) {
            onOverridePriceChange(Math.round(orderCost * (1 + pct / 100)))
        }
    }

    // Toda mudança de valor é anunciada: complemento e estorno (cobrança paga) porque têm
    // consequência financeira, e a correção simples (cobrança em aberto) porque o operador
    // precisa ver que a cobrança acompanha o novo valor. Valor inalterado não avisa nada.
    const adjustmentPreview = useMemo(() => {
        if (!isEditing || !existingBilling || chargedPrice <= 0) return null
        const preview = previewAppointmentAdjustment(
            existingBilling,
            chargedPrice,
            adjustmentSource
        )
        if (!preview || preview.action === 'NONE') return null
        const action = preview.action
        return { ...preview, action }
    }, [isEditing, existingBilling, chargedPrice, adjustmentSource])

    /**
     * A forma de pagamento escolhida aqui NÃO tem efeito nesta edição.
     *
     * A cobrança já emitida não troca de método por este formulário: quem manda no
     * link e no QR que o paciente tem é ela. E a edição só chega ao método em dois
     * casos — quando CRIA a cobrança (atendimento que ficou sem nenhuma viva) e
     * quando o valor sobe numa cobrança paga e nasce um COMPLEMENTO, que é dele que
     * a forma passa a ser.
     *
     * Fora desses, oferecer Pix e cartões é pedir uma decisão que o salvamento
     * descarta — o operador escolhe, vê marcado, e nada acontece.
     */
    const cobrancaViva =
        !!existingBilling && !isClosedBillingStatus(existingBilling.status)
    /**
     * PROCEDIMENTO sem cobrança viva: a edição também não chega ao método.
     *
     * Exame e consulta criam a cobrança quando o operador escolhe a forma
     * (`generateBilling`); o procedimento não tem esse caminho — quem gera é o botão
     * "Gerar cobrança" da tela do procedimento. Oferecer a forma aqui seria o mesmo
     * silêncio: escolher, ver marcado, e o salvamento descartar.
     */
    const procedimentoSemCobranca =
        isEditing && serviceType === 'PROCEDURE' && !cobrancaViva

    const formaSemEfeito =
        isEditing &&
        ((cobrancaViva && adjustmentPreview?.action !== 'EXTRA_CHARGE') ||
            procedimentoSemCobranca)

    /**
     * A barra flutuante resume a linha "Valor Final" quando ela sai da viewport —
     * e essa linha vive dentro do bloco de valores. Escondido o bloco (retorno sem
     * cobrança), o observer se desconecta com o último `finalRowVisible` que teve:
     * quem já tinha rolado a página ficava com a barra fixa no rodapé mostrando
     * valor e desconto do que ninguém vai cobrar.
     */
    const showFloatingBar =
        effectivePrice > 0 && !returnWithoutBilling && !finalRowVisible

    useEffect(() => {
        onFloatingBarChange?.(showFloatingBar)
        return () => {
            onFloatingBarChange?.(false)
        }
    }, [showFloatingBar, onFloatingBarChange])

    /**
     * Menor valor que o operador pode digitar: o preço de tabela menos a margem do
     * item (o `maxDiscount` do catálogo). Digitar o VALOR existe porque desconto
     * redondo raramente dá valor redondo (7,3% de R$ 137), e é o valor que precisa
     * fechar.
     *
     * Procedimento em ORÇAMENTO negocia livre — piso zero.
     */
    const priceFloor = useMemo(() => {
        if (canEditFinalPrice) return 0
        if (maxDiscount <= 0 || effectivePrice <= 0) return effectivePrice
        // Mesma fórmula do desconto em porcentagem, para o limite bater no centavo.
        return effectivePrice - Math.round((effectivePrice * maxDiscount) / 100)
    }, [canEditFinalPrice, effectivePrice, maxDiscount])

    /**
     * Para CIMA não há teto, e por isso o lápis aparece sempre que há preço.
     *
     * `maxDiscount` é limite de DESCONTO, e subir o preço não é desconto: cirurgia se
     * negocia caso a caso (equipe maior, material que o item não previa, hospital
     * mais caro) e a tabela é piso comercial, não preço máximo. Já houve teto aqui —
     * primeiro no próximo real inteiro, depois em `+maxDiscount%` — e os dois
     * barravam negociação legítima. O que o item sem margem cadastrada não aceita é
     * DESCER, e disso cuida o piso.
     */
    const canEditPrice = canEditFinalPrice || effectivePrice > 0

    /**
     * O quanto o VALOR digitado se afasta da tabela, em porcentagem — negativo é
     * desconto, positivo é acréscimo.
     *
     * Sem isto o operador digita o valor e não vê a que percentual ele corresponde —
     * o campo "Desconto (%)" continua mostrando o percentual antigo, que já não
     * descreve o preço na tela. Duas casas porque o valor raramente cai em
     * porcentagem redonda (é justamente o motivo de existir a edição por valor).
     */
    const overrideDeltaPercent = useMemo(() => {
        if (overridePrice == null || effectivePrice <= 0) return null
        const pct = ((overridePrice - effectivePrice) / effectivePrice) * 100
        if (pct === 0) return null
        return Math.round(pct * 100) / 100
    }, [overridePrice, effectivePrice])

    /**
     * Só a parte de DESCONTO do afastamento, para o campo "Desconto (%)": valor
     * acima da tabela é acréscimo, e ali o desconto é zero — não um número negativo.
     */
    const overrideDiscountPercent =
        overrideDeltaPercent != null && overrideDeltaPercent < 0
            ? Math.abs(overrideDeltaPercent)
            : null

    const formatPercent = (value: number) =>
        `${value.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}%`

    /** "-7,3%" ou "+4%", conforme o valor digitado ficou abaixo ou acima da tabela. */
    const formatSignedPercent = (value: number) =>
        `${value > 0 ? '+' : '-'}${formatPercent(Math.abs(value))}`

    /** Só o piso limita: para cima o valor é livre. */
    const clampPrice = (cents: number) => Math.max(cents, priceFloor)

    /**
     * Valor sem centavos, para facilitar o troco no balcão.
     *
     * Sempre para CIMA (39,50 → 40,00; 32,30 → 33,00): é o que tira o troco sem
     * conceder desconto, e para cima não há teto que atrapalhe.
     */
    const roundedPrice = useMemo(() => {
        const current = overridePrice != null ? overridePrice : finalPrice
        if (current <= 0) return null
        if (current % 100 === 0) return null // já está redondo
        return Math.ceil(current / 100) * 100
    }, [overridePrice, finalPrice])

    const handleRoundPrice = () => {
        if (roundedPrice == null) return
        onOverridePriceChange(roundedPrice)
        setEditPriceValue(currencyFormat(roundedPrice.toString(), true))
    }

    const handleStartEdit = () => {
        const current = overridePrice != null ? overridePrice : finalPrice
        setEditPriceValue(currencyFormat(current.toString(), true))
        setEditingPrice(true)
    }
    // Atualiza o valor de venda EM TEMPO REAL (a cada tecla) para que o markup
    // recalcule na hora — não só no blur.
    const handleEditPriceChange = (raw: string) => {
        const formatted = currencyFormat(raw, true)
        setEditPriceValue(formatted)
        const cents = parseCurrencyToCents(formatted)
        onOverridePriceChange(cents > 0 ? cents : null)
    }
    /**
     * O limite é aplicado ao CONFIRMAR, não a cada tecla: clampar durante a
     * digitação faria "9" virar o piso antes de o operador terminar de escrever
     * "90,00".
     */
    const handleCommitEdit = () => {
        const digitado = parseCurrencyToCents(editPriceValue)
        if (digitado <= 0) {
            onOverridePriceChange(null)
            setEditPriceValue('')
            setEditingPrice(false)
            return
        }
        const ajustado = clampPrice(digitado)
        if (ajustado !== digitado) {
            toast.warning(
                maxDiscount > 0
                    ? `Valor mínimo com o desconto máximo de ${maxDiscount}%: ${formatCentsLabel(priceFloor)}.`
                    : `Este item não tem desconto máximo cadastrado, então o valor não pode ficar abaixo de ${formatCentsLabel(priceFloor)}.`
            )
        }
        setEditPriceValue(currencyFormat(ajustado.toString(), true))
        onOverridePriceChange(ajustado)
        setEditingPrice(false)
    }
    const handleCancelEdit = () => {
        setEditingPrice(false)
        setEditPriceValue('')
        onOverridePriceChange(null)
    }
    const handleResetPrice = () => {
        onOverridePriceChange(null)
        setEditPriceValue('')
    }

    if (!hasCatalogSelected) return null

    /**
     * Os seletores aparecem SEMPRE, salvo dois casos: o de parceiro quando o
     * próprio usuário é parceiro, e o de profissional na intermediação fora da
     * cirurgia (ver `hideProfessionalSelector`).
     *
     * Antes eles dependiam de o item ter vínculos: item sem profissional atrelado
     * escondia o seletor inteiro, e não havia como informar quem faz o
     * procedimento — problema recorrente em orçamento de procedimento, onde o
     * item novo raramente já nasce com os vínculos prontos. Exigir o vínculo para
     * poder *escolher* invertia a ordem natural: primeiro se agenda, depois se
     * ajusta o catálogo.
     *
     * A busca também é livre (`items` indefinido): o vínculo agora só serve para
     * enfeitar o item com o preço (`priceLabelById`), não para restringir a lista.
     */
    const showProfessionalSelector = !hideProfessionalSelector
    const showPartnerSelector = !hidePartnerSelector

    return (
        <div className="mt-4">
            {/* Profissional e Parceiro */}
            {(showProfessionalSelector || showPartnerSelector) && (
                <div
                    className={`grid grid-cols-1 gap-4 ${
                        // Duas colunas só quando há dois campos: com um só, a
                        // segunda coluna ficaria vazia e o campo espremido em
                        // metade da largura.
                        showProfessionalSelector && showPartnerSelector
                            ? 'md:grid-cols-2'
                            : ''
                    }`}
                >
                    {showProfessionalSelector && (
                        <div className="flex flex-col gap-1">
                            <ProfessionalSearchSelector
                                value={
                                    professionalId
                                        ? parseInt(professionalId)
                                        : null
                                }
                                onChange={pid =>
                                    onProfessionalIdChange(
                                        pid ? pid.toString() : ''
                                    )
                                }
                                label="Profissional (opcional)"
                                placeholder="Buscar profissional..."
                                excludeDocument={partnerDocument}
                                // Busca livre (`items` indefinido) em TODOS os
                                // profissionais: quem atende nem sempre está
                                // atrelado ao item, e exigir o vínculo só para
                                // poder escolher trava o agendamento. O vínculo
                                // segue valendo para o PREÇO (`priceLabelById`).
                                //
                                // Vale só para profissional — parceiro continua
                                // restrito a quem faz o item (ver abaixo).
                                priceLabelById={
                                    isItemOnly
                                        ? undefined
                                        : Object.fromEntries(
                                              catalogProfessionals
                                                  .filter(
                                                      (cp: any) =>
                                                          cp.sellingPrice ||
                                                          cp.price
                                                  )
                                                  .map((cp: any) => [
                                                      cp.professionalId,
                                                      formatCentsLabel(
                                                          cp.sellingPrice ||
                                                              cp.price
                                                      ),
                                                  ])
                                          )
                                }
                            />
                            {professionalId &&
                                !isItemOnly &&
                                (serviceType === 'PROCEDURE' ? (
                                    // Editável: médicos nem sempre cobram o mesmo por
                                    // procedimento. Vazio = usa o valor do catálogo.
                                    <div className="px-1">
                                        <label className="label py-1">
                                            <span className="label-text text-xs text-base-content/70">
                                                Valor do profissional
                                            </span>
                                        </label>
                                        <input
                                            type="text"
                                            inputMode="decimal"
                                            className="input input-bordered input-sm w-full"
                                            placeholder={
                                                profCostPrice > 0
                                                    ? `Catálogo: ${formatCentsLabel(profCostPrice)}`
                                                    : 'R$ 0,00'
                                            }
                                            value={professionalAmountOverride}
                                            onChange={e =>
                                                onProfessionalAmountOverrideChange?.(
                                                    currencyFormat(
                                                        e.target.value,
                                                        true
                                                    ),
                                                    // Digitou: passa a valer para o
                                                    // preço também.
                                                    false
                                                )
                                            }
                                        />
                                        {professionalAmountOverride &&
                                            profCostPrice > 0 && (
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        onProfessionalAmountOverrideChange?.(
                                                            ''
                                                        )
                                                    }
                                                    className="text-xs link link-hover text-base-content/60 mt-1"
                                                >
                                                    Restaurar valor do catálogo
                                                    (
                                                    {formatCentsLabel(
                                                        profCostPrice
                                                    )}
                                                    )
                                                </button>
                                            )}
                                    </div>
                                ) : (
                                    profPrice > 0 && (
                                        <p className="text-xs text-base-content/70 px-1">
                                            Valor do profissional:{' '}
                                            <span className="font-semibold text-base-content">
                                                {formatCentsLabel(profPrice)}
                                            </span>
                                        </p>
                                    )
                                ))}
                        </div>
                    )}

                    {showPartnerSelector && (
                        <PartnerSelector
                            value={partnerId}
                            onChange={pid => onPartnerIdChange(pid)}
                            label="Parceiro (opcional)"
                            placeholder="Buscar parceiro..."
                            // Continua restrito a quem faz o item: escolher o item
                            // define quais parceiros o atendem.
                            items={catalogPartners
                                .filter((cp: any) => cp.partner)
                                .map((cp: any) => cp.partner)}
                            priceLabelById={
                                isItemOnly
                                    ? undefined
                                    : Object.fromEntries(
                                          catalogPartners
                                              .map((cp: any) => {
                                                  // Preço mostrado ao lado de cada
                                                  // parceiro no seletor: também
                                                  // por item e quantidade.
                                                  const price =
                                                      itensComQuantidade
                                                          ? getPartnerTotalForItems(
                                                                cp.partnerId,
                                                                itensComQuantidade,
                                                                catalogs!
                                                            )
                                                          : cp.sellingPrice ||
                                                            cp.price ||
                                                            0
                                                  return [cp.partnerId, price]
                                              })
                                              .filter(
                                                  ([, price]: any) => price > 0
                                              )
                                              .map(([id, price]: any) => [
                                                  id,
                                                  formatCentsLabel(price),
                                              ])
                                      )
                            }
                        />
                    )}
                </div>
            )}

            {/* Composição de preço - quando ambos selecionados e não é item_only */}
            {!isItemOnly && hasBothSelected && (
                /* Escolha rotineira, não alerta: o amarelo de warning fazia o
                   bloco parecer um erro permanente na tela. */
                <div className="mt-4 rounded-lg border border-base-300 bg-base-200/40 p-3">
                    <p className="mb-2 text-sm font-medium">
                        Profissional e parceiro selecionados. Como compor o
                        valor?
                    </p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        {[
                            {
                                value: 'item' as PriceComposition,
                                label: 'Valor do Item',
                                price: basePrice + hospitalFee,
                            },
                            {
                                value: 'partner' as PriceComposition,
                                label: 'Valor do Parceiro',
                                price: partnerPrice + extraTotal + hospitalFee,
                            },
                            {
                                value: 'professional' as PriceComposition,
                                label: 'Valor do Profissional',
                                price: profPrice + extraTotal + hospitalFee,
                            },
                            {
                                value: 'sum' as PriceComposition,
                                label: 'Somar Ambos',
                                price:
                                    partnerPrice +
                                    profPrice +
                                    extraTotal +
                                    hospitalFee,
                            },
                        ].map(opt => (
                            <label
                                key={opt.value}
                                className={`flex flex-col items-center p-2 rounded-lg border cursor-pointer transition-colors text-center ${
                                    priceComposition === opt.value
                                        ? 'border-secondary bg-secondary/5'
                                        : 'border-base-300 hover:border-base-content/30'
                                }`}
                            >
                                <input
                                    type="radio"
                                    name="priceComposition"
                                    className="radio radio-secondary radio-xs mb-1"
                                    checked={priceComposition === opt.value}
                                    onChange={() =>
                                        onPriceCompositionChange(opt.value)
                                    }
                                />
                                <span className="text-xs font-medium">
                                    {opt.label}
                                </span>
                                <span className="text-xs text-base-content/60">
                                    {formatCentsLabel(opt.price)}
                                </span>
                            </label>
                        ))}
                    </div>
                </div>
            )}

            {/* Valores — bloco próprio com borda, separado da escolha de
                profissional/parceiro para não confundir com o valor do profissional. */}
            {effectivePrice > 0 && !returnWithoutBilling && (
                <div className="flex flex-col gap-2 mt-4 p-4 rounded-lg border border-base-300 bg-base-200/30">
                    {/* O rótulo e o número têm de andar juntos: quando a linha diz
                        "Custo", ela mostra o CUSTO (`price` do vínculo, o que vira
                        repasse) — não o valor composto, que é a venda. */}
                    <div className="flex items-center gap-4">
                        <span className="text-sm text-base-content/70">
                            {showOrderMarkup ? 'Custo:' : 'Valor:'}
                        </span>
                        <span className="font-medium">
                            {formatCentsLabel(
                                showOrderMarkup ? orderCost : effectivePrice
                            )}
                        </span>
                    </div>
                    {showOrderMarkup && (
                        <div className="flex flex-wrap items-center gap-3">
                            <span className="text-sm text-base-content/70">
                                Markup:
                            </span>
                            <input
                                type="number"
                                step="0.01"
                                className="input input-bordered input-sm w-24"
                                placeholder="0"
                                value={markupInput}
                                onChange={e =>
                                    handleMarkupInput(e.target.value)
                                }
                                onFocus={() => setMarkupFocused(true)}
                                onBlur={() => setMarkupFocused(false)}
                            />
                            <span className="text-sm text-base-content/60">
                                %
                            </span>
                            {orderMarkup != null && orderMarkup !== 0 && (
                                <span className="text-xs text-base-content/50">
                                    Lucro:{' '}
                                    {formatCentsLabel(
                                        submittedPrice - orderCost
                                    )}
                                    {/* O percentual do lucro é a MARGEM (sobre a
                                            venda), não o markup do campo ao lado (sobre
                                            o custo) — são números diferentes para o
                                            mesmo negócio, e mostrar só o markup fazia
                                            ler 102% de markup como 102% de lucro. */}
                                    {profitMargin(
                                        submittedPrice,
                                        submittedPrice - orderCost
                                    ) != null && (
                                        <>
                                            {' ('}
                                            {profitMargin(
                                                submittedPrice,
                                                submittedPrice - orderCost
                                            )}
                                            {'% de margem)'}
                                        </>
                                    )}
                                </span>
                            )}
                        </div>
                    )}

                    {hospitalFee > 0 && (
                        <div className="flex items-center gap-4 text-xs text-base-content/60">
                            <span>Inclui hospital:</span>
                            <span className="font-medium">
                                {formatCentsLabel(hospitalFee)}
                            </span>
                        </div>
                    )}
                    {/* Sem teto configurado no catálogo, não há desconto: o campo
                        some em vez de aparecer desabilitado, para não sugerir que
                        basta destravar algo. O backend recusa do mesmo jeito. */}
                    {maxDiscount > 0 && (
                        <div className="flex items-center gap-4">
                            <label className="text-sm text-base-content/70">
                                Desconto (%):
                                <span className="text-xs text-base-content/50 ml-1">
                                    (máx: {maxDiscount}%)
                                </span>
                            </label>
                            {/* Com valor digitado o desconto passa a ser derivado
                                dele: manter o campo editável mostraria dois
                                percentuais diferentes para o mesmo preço. */}
                            <input
                                type="number"
                                step="0.01"
                                min="0"
                                max={maxDiscount}
                                disabled={overridePrice != null}
                                title={
                                    overridePrice != null
                                        ? 'Definido pelo valor digitado. Desfaça a edição do valor para usar o percentual.'
                                        : undefined
                                }
                                className="input input-bordered input-sm w-24 disabled:opacity-60"
                                placeholder="0"
                                value={
                                    overridePrice != null
                                        ? (overrideDiscountPercent ?? 0)
                                        : discount
                                }
                                onChange={e => {
                                    const val = e.target.value
                                    if (val === '') {
                                        onDiscountChange('')
                                        return
                                    }
                                    const num = parseFloat(val)
                                    if (isNaN(num) || num < 0) return
                                    onDiscountChange(
                                        String(Math.min(num, maxDiscount))
                                    )
                                }}
                            />
                        </div>
                    )}
                    <div
                        ref={setFinalRowEl}
                        className="flex items-center gap-4"
                    >
                        <span className="text-sm font-semibold">
                            {showOrderMarkup
                                ? 'Valor de venda:'
                                : 'Valor Final:'}
                        </span>
                        {editingPrice && canEditPrice && !showFloatingBar ? (
                            <div className="flex items-center gap-2">
                                <input
                                    type="text"
                                    className="input input-bordered input-sm w-40"
                                    value={editPriceValue}
                                    onChange={e =>
                                        handleEditPriceChange(e.target.value)
                                    }
                                    autoFocus
                                    onKeyDown={e => {
                                        if (e.key === 'Enter')
                                            handleCommitEdit()
                                        if (e.key === 'Escape')
                                            handleCancelEdit()
                                    }}
                                    onBlur={handleCommitEdit}
                                />
                                <button
                                    type="button"
                                    className="btn btn-ghost btn-xs"
                                    onClick={handleCancelEdit}
                                    title="Voltar ao valor calculado"
                                >
                                    <MdClose className="text-sm" />
                                </button>
                                {overrideDeltaPercent != null && (
                                    <span
                                        className={`text-xs font-medium ${overrideDeltaPercent > 0 ? 'text-warning' : 'text-success'}`}
                                    >
                                        ={' '}
                                        {formatPercent(
                                            Math.abs(overrideDeltaPercent)
                                        )}{' '}
                                        {overrideDeltaPercent > 0
                                            ? 'de acréscimo'
                                            : 'de desconto'}
                                    </span>
                                )}
                                {!canEditFinalPrice && (
                                    <span className="text-xs text-base-content/50">
                                        mín. {formatCentsLabel(priceFloor)}
                                        {maxDiscount > 0
                                            ? ` (desconto máx. ${maxDiscount}%)`
                                            : ' (sem desconto cadastrado)'}
                                    </span>
                                )}
                            </div>
                        ) : (
                            <div className="flex items-center gap-2">
                                <span className="text-lg font-bold text-secondary">
                                    {formatCentsLabel(
                                        overridePrice != null
                                            ? overridePrice
                                            : finalPrice
                                    )}
                                </span>
                                {/* O valor foi digitado: mostra a que desconto ele
                                    equivale, senão o percentual fica invisível. */}
                                {overrideDeltaPercent != null && (
                                    <span
                                        className={`badge badge-sm ${overrideDeltaPercent > 0 ? 'badge-warning' : 'badge-success'}`}
                                    >
                                        {formatSignedPercent(
                                            overrideDeltaPercent
                                        )}
                                    </span>
                                )}
                                {!showFloatingBar && canEditPrice && (
                                    <button
                                        type="button"
                                        className="btn btn-ghost btn-xs gap-1"
                                        onClick={handleStartEdit}
                                        title="Editar valor final"
                                    >
                                        <MdEdit className="text-sm" />
                                    </button>
                                )}
                                {!showFloatingBar &&
                                    canEditPrice &&
                                    roundedPrice != null && (
                                        <button
                                            type="button"
                                            className="btn btn-ghost btn-xs gap-1 text-info"
                                            onClick={handleRoundPrice}
                                            title="Tira os centavos para facilitar o troco"
                                        >
                                            <MdCalculate className="text-sm" />
                                            {formatCentsLabel(roundedPrice)}
                                        </button>
                                    )}
                                {overridePrice != null && (
                                    <>
                                        {!showFloatingBar && (
                                            <button
                                                type="button"
                                                className="btn btn-ghost btn-xs text-warning gap-1"
                                                onClick={handleResetPrice}
                                                title="Desfazer edição e voltar ao valor calculado"
                                            >
                                                <MdUndo className="text-sm" />
                                                Desfazer
                                            </button>
                                        )}
                                    </>
                                )}
                            </div>
                        )}
                    </div>

                    {/* O que o pagador desembolsa na forma escolhida. Fica colado no
                        Valor Final porque é a pergunta seguinte do paciente — antes o
                        operador tinha de procurar o número na lista de formas abaixo,
                        e o resumo do topo continuava anunciando o valor sem taxa. */}
                    {payerQuote && payerQuote.total !== chargedPrice && (
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                            <span className="text-sm text-base-content/70">
                                {payerTotalLabel(payerQuote)}:
                            </span>
                            <span className="text-base font-bold text-base-content">
                                {formatCentsLabel(payerQuote.total)}
                            </span>
                            {payerQuote.installments > 1 && (
                                <span className="text-xs text-base-content/60">
                                    {payerQuote.installments}x de{' '}
                                    {formatCentsLabel(
                                        payerQuote.installmentAmount
                                    )}
                                </span>
                            )}
                        </div>
                    )}

                    {adjustmentPreview && (
                        <BillingAdjustmentNotice
                            action={adjustmentPreview.action}
                            amount={adjustmentPreview.amount}
                            currentTotal={adjustmentPreview.currentTotal}
                            newTotal={chargedPrice}
                            shared={adjustmentPreview.shared}
                            groupCurrentTotal={
                                adjustmentPreview.groupCurrentTotal
                            }
                            groupNewTotal={adjustmentPreview.groupNewTotal}
                            groupCount={adjustmentPreview.groupCount}
                            coveredByHealthPlan={
                                existingBilling?.payerType === 'HEALTH_PLAN'
                            }
                        />
                    )}

                    {/* O valor acima é o do serviço; o que o paciente paga depende da
                        forma escolhida. Sem isto o operador fecha "R$ 300 no Pix" e a
                        cobrança sai com outro número. Só na criação: com a cobrança já
                        existente, o valor verdadeiro é o dela, não uma simulação nova. */}
                    {/* Também na EDIÇÃO: o paciente que desistiu de 3x e quer 4x é
                        resolvido aqui, sem ida à tela de cobrança. Com a cobrança já
                        existente o valor verdadeiro é o dela — o componente só troca
                        o parcelamento combinado. */}
                    {/* ORÇAMENTO não tem forma de pagamento: nada é cobrado até
                        alguém aprovar, e a aprovação é que decide como se paga (pelo
                        fluxo normal ou pelo flex). Oferecer aqui faria o operador
                        fechar "R$ 300 no Pix" num documento que é só proposta.

                        Somando numa cobrança existente: a forma de pagamento também sai
                        de cena. O total ainda não está fechado — falta o que o operador
                        vai agendar em seguida —, e escolher agora gravaria um método na
                        cobrança que impediria os próximos atendimentos de somar nela. */}
                    {hidePaymentOptions ? null : isBudgetMode ? null : returnWithoutBilling ? null : formaSemEfeito ? (
                        <div className="mt-3 flex flex-col gap-3">
                            <div className="rounded-xl border border-base-300 bg-base-200/40 px-4 py-3 text-sm text-base-content/70">
                                {procedimentoSemCobranca ? (
                                    <>
                                        Este procedimento está{' '}
                                        <strong>sem cobrança</strong>. A forma
                                        de pagamento é escolhida ao gerar a
                                        cobrança, pela tela do procedimento.
                                    </>
                                ) : (
                                    <>
                                        A <strong>forma de pagamento</strong>{' '}
                                        desta cobrança não muda por aqui — ela
                                        já foi emitida, e o link que o paciente
                                        tem depende dela. Para trocá-la, abra a
                                        cobrança.
                                    </>
                                )}
                            </div>
                            {onEnableFlex && (
                                <PaymentOptions
                                    amount={chargedPrice}
                                    context="scheduling"
                                    hideMethods
                                    onFlex={onEnableFlex}
                                    flexSelected={flexSelected}
                                />
                            )}
                        </div>
                    ) : groupingIntoBilling ? (
                        <div className="mt-3 rounded-xl border border-base-300 bg-base-200/40 px-4 py-3 text-sm text-base-content/70">
                            Este atendimento vai somar na cobrança que já está
                            aberta para o paciente. A{' '}
                            <strong>forma de pagamento</strong> é escolhida no
                            fim, na cobrança, com o total de todos os
                            atendimentos.
                        </div>
                    ) : (
                        <>
                            {/* A cobrança original está paga e o valor subiu: a forma
                                escolhida aqui é a da cobrança COMPLEMENTAR que vai
                                nascer, não a da original — que não muda mais. Sem
                                dizer isso, o operador entende que está trocando a
                                forma do que o paciente já pagou. */}
                            {adjustmentPreview?.action === 'EXTRA_CHARGE' && (
                                <p className="mt-3 px-1 text-xs text-base-content/60">
                                    A forma abaixo vale para a{' '}
                                    <strong>cobrança complementar</strong>. Sem
                                    escolher, ela nasce com a mesma forma da
                                    cobrança original.
                                </p>
                            )}
                            <PaymentOptions
                                amount={
                                    adjustmentPreview?.action === 'EXTRA_CHARGE'
                                        ? adjustmentPreview.amount
                                        : chargedPrice
                                }
                                context="scheduling"
                                className="mt-3"
                                selectedMethod={paymentMethod}
                                selectedInstallments={installmentCount}
                                onSelect={option =>
                                    onPaymentChoice?.(
                                        option.method,
                                        option.installments
                                    )
                                }
                                /* No complemento a cotação é do DELTA, não do
                               atendimento: alimentar o resumo com ela faria o
                               "Total no Pix" da tela mostrar o valor da diferença
                               onde o operador lê o total. */
                                onQuoteChange={
                                    adjustmentPreview?.action === 'EXTRA_CHARGE'
                                        ? undefined
                                        : setPaymentQuote
                                }
                                onCustomize={onPaymentCustom}
                                onFlex={onEnableFlex}
                                flexSelected={flexSelected}
                                onDefer={onPaymentDefer}
                                deferSelected={deferBillingSelected}
                            />
                        </>
                    )}
                </div>
            )}

            {showFloatingBar && (
                <div className="fixed bottom-0 left-0 right-0 z-40 bg-base-100/95 backdrop-blur border-t border-base-300 shadow-lg">
                    {/* Mesma largura do container da página, senão a barra fica
                        deslocada em relação ao formulário que ela resume. */}
                    <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-x-6 gap-y-2 px-4 py-3">
                        <div className="flex items-center gap-2 text-sm">
                            <span className="text-base-content/60">
                                {showOrderMarkup ? 'Custo:' : 'Valor:'}
                            </span>
                            <span className="font-medium">
                                {formatCentsLabel(
                                    showOrderMarkup ? orderCost : effectivePrice
                                )}
                            </span>
                        </div>
                        {showOrderMarkup && (
                            <div className="flex items-center gap-2 text-sm">
                                <span className="text-base-content/60">
                                    Markup:
                                </span>
                                <input
                                    type="number"
                                    step="0.01"
                                    className="input input-bordered input-sm w-20"
                                    placeholder="0"
                                    value={markupInput}
                                    onChange={e =>
                                        handleMarkupInput(e.target.value)
                                    }
                                    onFocus={() => setMarkupFocused(true)}
                                    onBlur={() => setMarkupFocused(false)}
                                />
                                <span className="text-base-content/50">%</span>
                            </div>
                        )}
                        {/* Mesma regra do bloco acima: sem teto, sem campo. */}
                        {maxDiscount > 0 && (
                            <div className="flex items-center gap-2 text-sm">
                                <label className="text-base-content/60">
                                    Desconto (%)
                                    <span className="text-xs text-base-content/40 ml-1">
                                        (max: {maxDiscount}%)
                                    </span>
                                </label>
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    max={maxDiscount}
                                    disabled={overridePrice != null}
                                    title={
                                        overridePrice != null
                                            ? 'Definido pelo valor digitado. Desfaça a edição do valor para usar o percentual.'
                                            : undefined
                                    }
                                    className="input input-bordered input-sm w-20 disabled:opacity-60"
                                    placeholder="0"
                                    value={
                                        overridePrice != null
                                            ? (overrideDiscountPercent ?? 0)
                                            : discount
                                    }
                                    onChange={e => {
                                        const val = e.target.value
                                        if (val === '') {
                                            onDiscountChange('')
                                            return
                                        }
                                        const num = parseFloat(val)
                                        if (isNaN(num) || num < 0) return
                                        onDiscountChange(
                                            String(Math.min(num, maxDiscount))
                                        )
                                    }}
                                />
                            </div>
                        )}
                        <div className="flex items-center gap-2">
                            <span className="text-sm font-semibold text-base-content/70">
                                {showOrderMarkup
                                    ? 'Valor de venda:'
                                    : 'Valor Final:'}
                            </span>
                            {editingPrice && canEditPrice ? (
                                <div className="flex items-center gap-2">
                                    <input
                                        type="text"
                                        className="input input-bordered input-sm w-40"
                                        value={editPriceValue}
                                        onChange={e =>
                                            setEditPriceValue(
                                                currencyFormat(
                                                    e.target.value,
                                                    true
                                                )
                                            )
                                        }
                                        autoFocus
                                        onKeyDown={e => {
                                            if (e.key === 'Enter')
                                                handleCommitEdit()
                                            if (e.key === 'Escape')
                                                handleCancelEdit()
                                        }}
                                        onBlur={handleCommitEdit}
                                    />
                                    <button
                                        type="button"
                                        className="btn btn-ghost btn-xs"
                                        onClick={handleCancelEdit}
                                        title="Voltar ao valor calculado"
                                    >
                                        <MdClose className="text-sm" />
                                    </button>
                                </div>
                            ) : (
                                <>
                                    <span className="text-lg font-bold text-secondary">
                                        {formatCentsLabel(
                                            overridePrice != null
                                                ? overridePrice
                                                : finalPrice
                                        )}
                                    </span>
                                    {overrideDeltaPercent != null && (
                                        <span
                                            className={`badge badge-sm ${overrideDeltaPercent > 0 ? 'badge-warning' : 'badge-success'}`}
                                        >
                                            {formatSignedPercent(
                                                overrideDeltaPercent
                                            )}
                                        </span>
                                    )}
                                    {canEditPrice && (
                                        <button
                                            type="button"
                                            className="btn btn-secondary btn-xs gap-1"
                                            onClick={handleStartEdit}
                                            title="Editar valor final"
                                        >
                                            <MdEdit className="text-sm" />
                                            Editar
                                        </button>
                                    )}
                                    {canEditPrice && roundedPrice != null && (
                                        <button
                                            type="button"
                                            className="btn btn-ghost btn-xs gap-1"
                                            onClick={handleRoundPrice}
                                            title={`Arredondar para ${formatCentsLabel(roundedPrice)} — tira os centavos para facilitar o troco`}
                                        >
                                            <MdCalculate className="text-sm" />
                                            {formatCentsLabel(roundedPrice)}
                                        </button>
                                    )}
                                    {overridePrice != null && (
                                        <>
                                            <button
                                                type="button"
                                                className="btn btn-ghost btn-xs text-warning gap-1"
                                                onClick={handleResetPrice}
                                                title="Desfazer edição e voltar ao valor calculado"
                                            >
                                                <MdUndo className="text-sm" />
                                                Desfazer
                                            </button>
                                        </>
                                    )}
                                </>
                            )}
                        </div>
                        {/* Mesma informação da seção, encolhida: com a barra visível o
                            bloco de formas de pagamento está fora da tela. */}
                        {payerQuote && payerQuote.total !== chargedPrice && (
                            <div className="flex items-center gap-2 text-sm">
                                <span className="text-base-content/60">
                                    {payerTotalLabel(payerQuote)}:
                                </span>
                                <span className="font-bold">
                                    {formatCentsLabel(payerQuote.total)}
                                </span>
                            </div>
                        )}
                        {adjustmentPreview && (
                            <BillingAdjustmentNotice
                                compact
                                action={adjustmentPreview.action}
                                amount={adjustmentPreview.amount}
                                currentTotal={adjustmentPreview.currentTotal}
                                newTotal={chargedPrice}
                                shared={adjustmentPreview.shared}
                                groupCurrentTotal={
                                    adjustmentPreview.groupCurrentTotal
                                }
                                groupNewTotal={adjustmentPreview.groupNewTotal}
                                groupCount={adjustmentPreview.groupCount}
                                coveredByHealthPlan={
                                    existingBilling?.payerType === 'HEALTH_PLAN'
                                }
                            />
                        )}
                    </div>
                </div>
            )}
        </div>
    )
}
